import { useState } from "react";
import { formatMoney } from "@tovi/shared";
import { Button, Modal, useTranslate } from "@tovi/ui";
import { labelOf, type Bill, type BillBook } from "@/lib/bills.ts";
import { unitCost, type Cart } from "@/lib/cart.ts";

/**
 * Merging two bills, and splitting one (`08-pos-terminal-and-qr.md` § A).
 *
 * Both live here rather than in the footer because both are rare and neither
 * ends the service: the footer's three buttons are hold, fire and settle, and a
 * fourth and fifth would push the ones a cashier presses every table.
 *
 * **Both need a connection**, and the dialog says so rather than queueing an
 * operation that could never drain. Merge names both bills by their server ids,
 * and split's new bill *is* the server's answer — neither is expressible as a
 * queued write against a bill that has not been created yet.
 *
 * Only fired lines can be split, because a line is moved by its order-item id
 * and an unfired line does not have one. A cashier who wants to separate
 * something not yet sent can simply ring it on the other bill.
 *
 * **Built from the spec text.** Without a capture, this is one dialog with two
 * sections rather than two entries in a menu that does not exist. Which lines
 * are chosen is a checkbox list; a real till may want to tap the lines in the
 * cart itself. Marked as the judgement call it is.
 */
export function BillActionsDialog({
  cart,
  book,
  online,
  onMerge,
  onSplit,
  onClose,
}: {
  cart: Cart;
  book: BillBook;
  online: boolean;
  onMerge: (sourceKeys: string[]) => void;
  onSplit: (itemIds: string[]) => void;
  onClose: () => void;
}) {
  const t = useTranslate();
  const [chosen, setChosen] = useState<string[]>([]);

  const others = book.bills.filter(
    (row) => row.key !== cart.bill?.key && row.orderId !== null && row.status !== "failed",
  );
  const fired = cart.lines.filter((line) => line.isFired);

  function toggle(itemId: string): void {
    setChosen((held) =>
      held.includes(itemId) ? held.filter((row) => row !== itemId) : [...held, itemId],
    );
  }

  return (
    <Modal open title={t("pos.billActions.title")} onClose={onClose}>
      <div className="flex flex-col gap-5">
        {!online ? (
          <p className="text-sm text-danger">{t("pos.billActions.needsConnection")}</p>
        ) : null}

        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold text-ink-body">{t("pos.billActions.merge")}</h3>
          <p className="text-xs text-ink-muted">{t("pos.billActions.mergeHint")}</p>
          {others.length === 0 ? (
            <p className="text-sm text-ink-muted">{t("pos.billActions.noOthers")}</p>
          ) : (
            <div className="flex flex-col gap-1">
              {others.map((other) => (
                <Button
                  key={other.key}
                  variant="secondary"
                  disabled={!online}
                  onClick={() => {
                    onMerge([other.key]);
                    onClose();
                  }}
                >
                  {nameOf(other, t("pos.cart.takeAway"))}
                </Button>
              ))}
            </div>
          )}
        </section>

        <section className="flex flex-col gap-2 border-t border-line pt-4">
          <h3 className="text-sm font-semibold text-ink-body">{t("pos.billActions.split")}</h3>
          <p className="text-xs text-ink-muted">{t("pos.billActions.splitHint")}</p>
          {fired.length === 0 ? (
            <p className="text-sm text-ink-muted">{t("pos.billActions.noFired")}</p>
          ) : (
            <>
              <ul className="flex flex-col gap-1">
                {fired.map((line) => (
                  <li key={line.key}>
                    <label className="flex items-center gap-2 text-sm text-ink-body">
                      <input
                        type="checkbox"
                        checked={chosen.includes(line.key)}
                        onChange={() => toggle(line.key)}
                      />
                      <span className="flex-1">{line.name}</span>
                      <span className="text-ink-muted">
                        {formatMoney(unitCost(line).toString(), cart.currency)}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
              <Button
                disabled={!online || chosen.length === 0}
                onClick={() => {
                  onSplit(chosen);
                  onClose();
                }}
              >
                {t("pos.billActions.splitDo")}
              </Button>
            </>
          )}
        </section>

        <div className="flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            {t("pos.billActions.back")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** A table's number, or the bag's own order number — whichever it has. */
function nameOf(bill: Bill, takeAway: string): string {
  return labelOf(bill) ?? bill.orderNo ?? takeAway;
}
